import { createContext } from 'react';
import {
	IItemForGetShortInfoWithStatus,
	IGetDishRestaurantProperties,
	IUpdateDishRestaurantProperties,
	IRestaurantPropertiesModifierGroup,
	TPortionSelectionMode,
} from 'types';
import { defaultPortionSelectionMode } from 'appConstants/dishes';

export type TTypeOfModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'toConfirmSubmit'
	| 'saveAsPatch'
	| 'assignSubcategories'
	| 'success'
	| 'error';

export interface IEditDishRestaurantPropertiesState {
	needRefetchRestPropsData: boolean;
	customerName: string;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	restaurantsWithPortionsIds: string[];
	IDSelectedRestaurant: Nullable<string>;
	dishId: string;
	dishName: string;
	isCreatedOnPos: boolean;
	isPortionEnabled: boolean;
	categories: IGetDishRestaurantProperties['data']['categories'];
	price: Nullable<number>;
	openPrice: Nullable<number>;
	openPriceMaxThreshold: Nullable<number>;
	hasOpenPrice: boolean;
	inStop: boolean;
	isCommentVisible: boolean;
	isFiscalized: boolean;
	hasSchedulePriority: boolean;
	taxPercent: Nullable<number>;
	isTaxPercentProtectedFromSync: boolean;
	activationSchedule: string;
	deactivationSchedule: string;
	IDsSelectedModifierGroups: string[];
	modifierGroupsWithAdditionalData: IRestaurantPropertiesModifierGroup[];
	hasPortions: boolean;
	portionSelectionMode: TPortionSelectionMode;
	hasPortionBasedModGroups: boolean;
	dataForSubmit: Nullable<IUpdateDishRestaurantProperties>;
	typeOfModalWindow: TTypeOfModalWindow;
}

export const editDishRestaurantPropertiesInitialState: IEditDishRestaurantPropertiesState = {
	needRefetchRestPropsData: false,
	customerName: '',
	fetchedRestaurants: [],
	restaurantsWithPortionsIds: [],
	IDSelectedRestaurant: null,
	dishId: '',
	dishName: '',
	isCreatedOnPos: false,
	isPortionEnabled: false,
	categories: [],
	price: null,
	openPrice: null,
	openPriceMaxThreshold: null,
	hasOpenPrice: false,
	inStop: false,
	isCommentVisible: false,
	isFiscalized: false,
	taxPercent: null,
	isTaxPercentProtectedFromSync: false,
	hasSchedulePriority: false,
	activationSchedule: '',
	deactivationSchedule: '',
	IDsSelectedModifierGroups: [],
	modifierGroupsWithAdditionalData: [],
	hasPortions: false,
	portionSelectionMode: defaultPortionSelectionMode,
	hasPortionBasedModGroups: false,
	dataForSubmit: null,
	typeOfModalWindow: null,
};

export interface IEditDishRestaurantPropertiesContext extends IEditDishRestaurantPropertiesState {
	refetchRestPropsData(value: boolean): void;
	setIDRestaurant(value: Nullable<string>): void;
	changePrice(value: Nullable<number>): void;
	changeOpenPrice(value: Nullable<number>): void;
	changeOpenPriceMaxThreshold(value: Nullable<number>): void;
	toggleHasOpenPrice(): void;
	toggleInStop(): void;
	toggleIsCommentVisible(): void;
	toggleIsFiscalized(): void;
	changePortionSelectionMode(value: TPortionSelectionMode): void;
	changeTaxPercent(value: Nullable<number>): void;
	toggleIsTaxPercentProtectedFromSync(): void;
	changeActivationSchedule(value: string): void;
	changeDeactivationSchedule(value: string): void;
	setModifierGroups(value: string[]): void;
	setModifierGroupsWithAdditionalData(value: IRestaurantPropertiesModifierGroup[]): void;
	setDataForSubmit(data: IUpdateDishRestaurantProperties): void;
	changeTypeOfModalWindow(value: TTypeOfModalWindow): void;
}

export const EditDishRestaurantPropertiesContext = createContext<IEditDishRestaurantPropertiesContext>({
	refetchRestPropsData: () => undefined,
	setIDRestaurant: () => undefined,
	changePrice: () => undefined,
	changeOpenPrice: () => undefined,
	changeOpenPriceMaxThreshold: () => undefined,
	toggleHasOpenPrice: () => undefined,
	toggleInStop: () => undefined,
	toggleIsCommentVisible: () => undefined,
	toggleIsFiscalized: () => undefined,
	changePortionSelectionMode: () => undefined,
	changeTaxPercent: () => undefined,
	toggleIsTaxPercentProtectedFromSync: () => undefined,
	changeActivationSchedule: () => undefined,
	changeDeactivationSchedule: () => undefined,
	setModifierGroups: () => undefined,
	setModifierGroupsWithAdditionalData: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	...editDishRestaurantPropertiesInitialState,
});
